import {inject, Injectable} from '@angular/core';
import type {
  Purchases as PurchasesType,
  Package as WebPackage
} from '@revenuecat/purchases-js';
import {
  CustomerInfo,
  PurchaseOptions,
  PurchaseProvider,
  PurchaseProviderConfig,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import {WebSdkMapper} from './web-sdk-mapper';
import {
  AppConfigService
} from '../../config/app-config.service';
import {
  LoggerService
} from '../logging/logger.service';
import {
  MetaPixelService
} from '../meta-attribution/meta-pixel.service';
import {
  MetaAttributionService
} from '../meta-attribution/meta-attribution.service';
import {
  ObservabilityLevel,
  ObservabilityService
} from '../observability/observability.service';

/**
 * Wraps the RevenueCat Web SDK (purchases-js)
 * behind the PurchaseProvider interface.
 * Uses WebSdkMapper for all type conversions.
 */
@Injectable()
export class WebPurchaseProvider
  implements PurchaseProvider {

  private readonly appConfig =
    inject(AppConfigService);
  private readonly logger = inject(LoggerService);
  private readonly metaPixel =
    inject(MetaPixelService);
  private readonly metaAttribution =
    inject(MetaAttributionService);
  private readonly observability =
    inject(ObservabilityService);
  private readonly mapper = new WebSdkMapper();

  private purchasesClass:
    typeof PurchasesType | null = null;
  private readonly sdkPackageCache =
    new Map<string, WebPackage>();
  private purchases: PurchasesType | null = null;
  private listener:
    ((info: CustomerInfo) => void) | null = null;

  private async loadSdk():
    Promise<typeof PurchasesType> {
    if (!this.purchasesClass) {
      const mod =
        await import('@revenuecat/purchases-js');
      this.purchasesClass = mod.Purchases;
    }
    return this.purchasesClass;
  }

  async configure(
    config: PurchaseProviderConfig
  ): Promise<void> {
    const apiKey =
      this.appConfig.revenueCatWebApiKey;
    if (!apiKey) {
      throw new Error(
        'revenueCatWebApiKey not configured'
      );
    }
    const Purchases = await this.loadSdk();
    this.purchases = Purchases.configure({
      apiKey,
      appUserId: config.appUserID
        || crypto.randomUUID()
    });
    this.logger.info(
      'WebPurchaseProvider: Configured'
    );
    if (config.appUserID) {
      this.reportIdentity('configure');
    }
  }

  async isConfigured(): Promise<boolean> {
    const Purchases = await this.loadSdk();
    return Purchases.isConfigured();
  }

  async logIn(appUserID: string): Promise<void> {
    const apiKey =
      this.appConfig.revenueCatWebApiKey;
    if (!apiKey) return;
    const Purchases = await this.loadSdk();
    this.purchases = Purchases.configure({
      apiKey, appUserId: appUserID
    });
    this.reportIdentity('login');
  }

  /**
   * Records the SDK's current app user id to Sentry, mirroring
   * CapacitorPurchaseProvider's identity reporting (HEC-338) so
   * Scenario 2 holds on web too. Always reported at info level: a
   * web anonymous id is a random UUID, not a `$RCAnonymousID:`
   * prefix, so there is no anonymous state to warn about here — the
   * mobile guard in CapacitorPurchaseProvider.ensureIdentified() has
   * no web equivalent for the same reason. A failed read must never
   * fail the configure()/logIn() call it is reporting on.
   */
  private reportIdentity(source: string): void {
    try {
      const appUserId = this.getInstance().getAppUserId();
      this.observability.trackSubscriptionIdentity(
        appUserId, false, ObservabilityLevel.INFO,
        { source, platform: 'web' }
      );
    } catch (error) {
      this.logger.warn(
        'WebPurchaseProvider: failed to read/report '
        + `the SDK identity for source "${source}"`,
        error
      );
    }
  }

  async setLogLevel(
    _level: string
  ): Promise<void> {
    // Web SDK log level is set globally
  }

  async getOfferings():
    Promise<SubscriptionPackage[]> {
    const instance = this.getInstance();
    const offerings =
      await instance.getOfferings();
    const packages =
      offerings.current?.availablePackages ?? [];
    const offeringIdentifier =
      offerings.current?.identifier ?? null;
    return packages.map(p => {
      const mapped = this.mapper.mapPackage(
        p, offeringIdentifier
      );
      this.sdkPackageCache.set(
        mapped.product.identifier, p
      );
      return mapped;
    });
  }

  async purchase(
    product: SubscriptionProduct,
    options?: PurchaseOptions
  ): Promise<{ customerInfo: CustomerInfo }> {
    const instance = this.getInstance();
    const cachedPkg =
      this.sdkPackageCache.get(
        product.identifier
      );
    if (!cachedPkg) {
      throw new Error(
        'Package not found for product: '
        + product.identifier
        + '. Call getOfferings() first.'
      );
    }
    const result = await instance.purchase({
      rcPackage: cachedPkg,
      customerEmail: options?.customerEmail
    });
    const mapped =
      this.mapper.mapCustomerInfo(
        result.customerInfo
      );
    if (this.listener) {
      this.listener(mapped);
    }
    return { customerInfo: mapped };
  }

  async addCustomerInfoUpdateListener(
    listener: (info: CustomerInfo) => void
  ): Promise<void> {
    this.listener = listener;
    // Web SDK has no push updates, so fetch
    // current state and fire the listener
    // immediately to emulate mobile behaviour.
    const instance = this.getInstance();
    const info =
      await instance.getCustomerInfo();
    listener(this.mapper.mapCustomerInfo(info));
  }

  async restorePurchases(): Promise<{
    customerInfo: CustomerInfo;
  }> {
    const instance = this.getInstance();
    const info =
      await instance.getCustomerInfo();
    return {
      customerInfo:
        this.mapper.mapCustomerInfo(info)
    };
  }

  // --- Code redemption ---

  isRedeemCodeAvailable(): boolean {
    return false;
  }

  async redeemCode(): Promise<void> {
    throw new Error(
      'Code redemption not supported on web'
    );
  }

  // --- Management ---

  isManagementAvailable(): boolean {
    return true;
  }

  async openManagementPage(): Promise<void> {
    const instance = this.getInstance();
    const info =
      await instance.getCustomerInfo();
    if (info.managementURL) {
      globalThis.open(
        info.managementURL, '_blank'
      );
    }
  }

  // --- Attribution ---

  async initialiseAttribution(): Promise<void> {
    try {
      const pixelId = this.appConfig.metaPixelId;
      if (pixelId) {
        this.metaPixel.initialise(pixelId);
        await this.metaPixel.whenReady();
      }

      const identifiers =
        await this.metaAttribution
          .getAttributionIdentifiers();
      if (identifiers.fbp) {
        await this.setAttributes({
          '$fbAnonId': identifiers.fbp
        });
        this.logger.info(
          'WebPurchaseProvider: '
          + 'Attribution set',
          { '$fbAnonId': identifiers.fbp }
        );
      }
    } catch (error) {
      this.logger.warn(
        'WebPurchaseProvider: '
        + 'Attribution failed',
        error
      );
    }
  }

  async setAttributes(
    attributes: Record<string, string | null>
  ): Promise<void> {
    const instance = this.getInstance();
    await instance.setAttributes(attributes);
  }

  async setFBAnonymousID(
    _fbAnonymousId: string
  ): Promise<void> { /* no-op on web */ }

  async collectDeviceIdentifiers():
    Promise<void> { /* no-op on web */ }

  // --- Private ---

  private getInstance(): PurchasesType {
    if (!this.purchases) {
      throw new Error(
        'WebPurchaseProvider not configured. '
        + 'Call configure() first.'
      );
    }
    return this.purchases;
  }
}
